import { NgIf } from '@angular/common';
import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { UploadService } from '@services/upload.service';
import { take } from 'rxjs';

import { singleOrderDetails } from '../../../../../../../@store/orders/orders.model';

@Component({
  selector: 'ciwc-order-single-attachment',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    NgIf,
    MatProgressSpinner,
  ],
  templateUrl: './order-single-attachment.component.html',
  styleUrl: './order-single-attachment.component.scss',
})
export class OrderSingleAttachmentComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Input() orderDetails!: singleOrderDetails;
  @Input() viewDisable!: boolean;
  @Input() shipmentIndex!: number;
  @Input() fieldName!: string; // 'shipmentPhoto' or 'dropShipAttachment'
  @Input() fieldLabel!: string; // 'Shipment Photo' or 'Drop Shipment Attachment'

  private uploadService = inject(UploadService);
  private fb = inject(FormBuilder);

  isUploading = signal(false);
  uploadError = signal('');

  get shipmentGroup(): FormGroup {
    return this.parentForm.get('shipments')?.get(this.shipmentIndex.toString()) as FormGroup;
  }

  get fieldUrlName(): string {
    return `${this.fieldName}Url`;
  }

  ngOnInit() {
    // Initialize fields if they don't exist
    const shipmentGroup = this.shipmentGroup;
    if (shipmentGroup) {
      if (!shipmentGroup.get(this.fieldName)) {
        shipmentGroup.addControl(this.fieldName, this.fb.control(''));
      }
      if (!shipmentGroup.get(this.fieldUrlName)) {
        shipmentGroup.addControl(this.fieldUrlName, this.fb.control(''));
      }
    }

    // If we have existing attachment in orderDetails, populate it
    if (this.orderDetails?.shipments?.[this.shipmentIndex]) {
      const shipment = this.orderDetails.shipments[this.shipmentIndex];
      const fieldValue = shipment[this.fieldName as keyof typeof shipment];
      const fieldUrlValue = shipment[this.fieldUrlName as keyof typeof shipment];

      if (fieldValue || fieldUrlValue) {
        this.shipmentGroup.patchValue({
          [this.fieldName]: fieldValue,
          [this.fieldUrlName]: fieldUrlValue,
        });
      }
    }
  }

  triggerFileInput() {
    const fileInput = document.getElementById(
      `fileInput-${this.shipmentIndex}-${this.fieldName}`,
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Update the form state immediately
      this.shipmentGroup.patchValue({
        [this.fieldUrlName]: '',
      });

      // Set loading state
      this.isUploading.set(true);
      this.uploadError.set('');

      // Force change detection
      this.shipmentGroup.updateValueAndValidity();

      this.uploadService
        .uploadFile(file)
        .pipe(take(1))
        .subscribe({
          next: (res) => {
            // Update the form state with the response
            this.shipmentGroup.patchValue({
              [this.fieldName]: res.name,
              [this.fieldUrlName]: res.url,
            });

            // Reset loading state
            this.isUploading.set(false);
            this.uploadError.set('');

            // Force change detection
            this.shipmentGroup.updateValueAndValidity();
          },
          error: (err) => {
            // Update the form state with the error
            this.shipmentGroup.patchValue({
              [this.fieldUrlName]: '',
            });

            // Set error state
            this.isUploading.set(false);
            this.uploadError.set('Failed to upload file. Please try again.');

            // Force change detection
            this.shipmentGroup.updateValueAndValidity();
          },
        });
    }
  }

  viewAttachment() {
    const shipment = this.shipmentGroup.value;
    if (shipment[this.fieldUrlName]) {
      window.open(shipment[this.fieldUrlName], '_blank');
      return;
    }
    const file = shipment[this.fieldName];
    if (!file) return;
    if (file instanceof File) {
      const blobUrl = URL.createObjectURL(file);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } else if (typeof file === 'string') {
      window.open(file, '_blank');
    }
  }

  removeAttachment() {
    this.shipmentGroup.patchValue({
      [this.fieldName]: '',
      [this.fieldUrlName]: '',
    });
  }
}
